import {makeAutoObservable, reaction, runInAction} from 'mobx'
import Agent from '../../api/Agent'
import agent from '../../api/Agent'
import {toast} from 'react-toastify'
import {RootStore} from '../RootStore'
import {AuditReportAttachmentFormValues, IAuditReportAttachment} from "../../models/AuditReport/AuditReportAttachment";
import dayjs from "dayjs";


export default class AuditReportAttachmentStore {
    rootStore: RootStore

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore
        makeAutoObservable(this)

        reaction(
            () => this.predicate.keys(),
            () => {
                this.page = 0
                this.auditReportAttachmentRegistry.clear()
                this.loadAuditReportAttachments()
            }
        )
    }

    auditReportAttachmentRegistry = new Map()
    auditReportAttachment: IAuditReportAttachment | null = null
    currentAuditReportId = '';

    auditReportAttachmentCount = 0
    page = 0
    limit = 5

    predicate = new Map()

    setCurrentAuditReportId = (id: string) => {
        this.currentAuditReportId = id;
    }

    setPredicate = (predicate?: string, value?: any) => {
        if (!predicate || predicate === '') {
            this.predicate.clear()
        } else {
            this.predicate.delete(predicate)
            if (!(value === undefined && value === '' && value === 'undefined'))
                this.predicate.set(predicate, value instanceof Date ? new Date(value).toISOString() : `${value}`)
        }
    }

    setPredicates = (predicates: {}) => {
        this.auditReportAttachmentRegistry.clear()
        let tmpArr = Object.entries(predicates)
        this.predicate = new Map(tmpArr)
    }

    get axiosParams() {
        const params = new URLSearchParams()
        params.append('limit', String(this.limit))
        params.append('offset', `${this.page ? this.page * this.limit : 0}`)
        this.predicate.forEach((value, key) => {
            if (!(value === '' || value === undefined || value === 'undefined') && typeof value !== 'object'){
                params.append(key, value)
            } else if (typeof value === 'object') {
                value?.forEach(val => params.append(key, val))
            }
        })
        return params
    }

    get totalPages() {
        return Math.ceil(this.auditReportAttachmentCount / this.limit)
    }

    get auditReportAttachmentsByDate() {
        return Array.from(this.auditReportAttachmentRegistry.values()).sort(
            (a, b) => dayjs(b.createdOn).isBefore(a.createdOn) ? -1 : 1
        )
    }

    get auditReportAttachmentList() {
        // @ts-ignore
        return Array.from(this.auditReportRegistry.values()).sort((a,b) => new Date(b.updatedOn) - new Date(a.updatedOn))
    }

    setPage = (page: number) => {
        this.page = page
    }

    setLimit = (limit: number) => {
        this.limit = limit
    }

    handleChangePage = (newPage: number, newRow: number) => {
        //sets page, we do -1 because the offset will be multiplied against
        this.setPage(newPage - 1)
        //sets the row limit
        this.setLimit(newRow)
        //clears all records in the registry so that next page didnt load the prev records
        this.auditReportAttachmentRegistry.clear()
        this.loadAuditReportAttachments()
    }

    clearAuditReportAttachments = () => {
        this.auditReportAttachmentRegistry.clear()
    }

    loadAuditReportAttachments = async () => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        runInAction(() => {
            this.rootStore.commonStore.setIsLoading(loadingId)
        })
        try {
            const auditReportEnvelope = await Agent.AuditReportAttachments.list(this.axiosParams)
            const {modelsDto, modelCount} = auditReportEnvelope

            runInAction(() => {
                this.clearAuditReportAttachments()
                modelsDto.forEach((auditAttachmentReport) => {
                    this.auditReportAttachmentRegistry.set(auditAttachmentReport.id, auditAttachmentReport)
                })
                this.auditReportAttachmentCount = modelCount
            })

            this.currentAuditReportId = '';
        } catch (error) {
            throw error
        } finally {
        }
    }

    getAuditReportAttachmentByAuditReportId = async (id?: string) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        try {
            const auditReportEnvelope = await Agent.AuditReportAttachments.listById(id || this.currentAuditReportId, this.axiosParams)
            const {modelsDto, modelCount} = auditReportEnvelope

            runInAction(() => {
                this.clearAuditReportAttachments()
                modelsDto.forEach((auditAttachmentReport) => {
                    this.auditReportAttachmentRegistry.set(auditAttachmentReport.id, auditAttachmentReport)
                })
                this.auditReportAttachmentCount = modelCount
            })
        } catch (error) {
            throw error
        } finally {
            runInAction(() => {
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
        }
    }

    createAuditReportAttachment = async (auditReportAttachment: AuditReportAttachmentFormValues) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()

        try {
            const newAuditReportAttachment = await Agent.AuditReportAttachments.upload(auditReportAttachment)
            await (!!this.currentAuditReportId ? this.getAuditReportAttachmentByAuditReportId(this.currentAuditReportId) : this.loadAuditReportAttachments())

            runInAction(() => {
                this.auditReportAttachmentRegistry.set(newAuditReportAttachment.id, newAuditReportAttachment)
                this.rootStore.commonStore.setIsLoading(loadingId)
                this.rootStore.modalStore.closeModal('AttachmentModal')
            })

            toast.success('Saved')
        } catch (error) {
            this.rootStore.errorStore.addError(error)
        } finally {
            runInAction(() => {
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
        }
    }

    downloadAuditReportById = async (id: string) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        let attachment;
        try {
            attachment = await agent.AuditReportAttachments.downloadById(id);
        } catch (error) {
            this.rootStore.errorStore.addError(error);
        } finally {
            this.rootStore.commonStore.setIsLoading(loadingId)
        }

        return attachment;
    }

    deleteAuditReportAttachment = async (id: string) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        try {
            await Agent.AuditReportAttachments.delete(id)
            runInAction(() => {
                this.auditReportAttachmentRegistry.delete(id)
                this.rootStore.commonStore.setIsLoading(loadingId)
                this.rootStore.modalStore.closeModal('AuditReportAttachmentStore')
            })
            toast.success('Deleted!')
        } catch (error) {
            runInAction(() => {
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
            this.rootStore.errorStore.addError(error)
        }
    }
}